import React, { useEffect, useMemo, useState } from "react";
import { Loading } from "../components/Loader";
import { Select } from "../components/Select";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import PaginationBar from "../components/PaginationBar";
import { fetchStores, selectStores } from "../features/stores/storesSlice";
import {
  fetchReturns,
  selectReturns,
  selectReturnsCount,
  selectReturnsError,
  selectReturnsPage,
  selectReturnsPageSize,
  selectReturnsStatus,
} from "../features/returns/returnsSlice";

const PAGE_SIZE = 50;

const REFUND_REASON_LABELS = {
  defect: "Брак",
  notSuitable: "Не подошло",
  wrongPrice: "Неверная цена",
  wrongItem: "Перепутан товар",
  other: "Другое",
};

function isoDate(d) {
  return new Date(d).toISOString().slice(0, 10);
}

function firstDayOfMonth() {
  const d = new Date();
  return isoDate(new Date(d.getFullYear(), d.getMonth(), 1));
}

function today() {
  return isoDate(new Date());
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ru-RU");
}

function deriveCashierName(row) {
  const first = (row?.cashier_first_name || "").trim();
  const last = (row?.cashier_last_name || "").trim();
  const fullName = (row?.cashier_full_name || `${first} ${last}`).trim();
  if (fullName) return fullName;
  const username = (row?.cashier_username || "").trim();
  if (username) return username;
  if (row?.cashier_id != null) return `ID ${row.cashier_id}`;
  return "—";
}

// Каждая транзакция-возврат может содержать несколько товаров — задаче нужен
// «список возвращённых товаров», поэтому разворачиваем на уровень товарных строк.
function flattenToProductRows(transactions) {
  const rows = [];
  for (const t of transactions || []) {
    const cashierName = deriveCashierName(t);
    const reasonLabel = REFUND_REASON_LABELS[t?.refund_reason] || t?.refund_reason || "—";
    const items = Array.isArray(t?.product_transactions) ? t.product_transactions : [];
    for (const item of items) {
      rows.push({
        key: `${t.id}-${item.product_id}`,
        transactionId: t.id,
        transactionDt: t.transaction_dt,
        storeName: t.store_name || "—",
        productName: item.product_name || "—",
        barcode: item.barcode || "—",
        quantity: Math.abs(Number(item.quantity) || 0),
        reasonLabel,
        cashierName,
      });
    }
  }
  return rows;
}

export default function ReturnsPage() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [storeId, setStoreId] = useState(() => searchParams.get("store_id") || "");
  const [startDate, setStartDate] = useState(() => searchParams.get("start_date") || firstDayOfMonth());
  const [endDate, setEndDate] = useState(() => searchParams.get("end_date") || today());
  const [page, setPage] = useState(() => {
    const n = Number(searchParams.get("page") || 1);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
  });

  const stores = useSelector(selectStores);
  const transactions = useSelector(selectReturns);
  const totalCount = useSelector(selectReturnsCount);
  const loadStatus = useSelector(selectReturnsStatus);
  const error = useSelector(selectReturnsError);
  const returnsPage = useSelector(selectReturnsPage);
  const returnsPageSize = useSelector(selectReturnsPageSize);

  useEffect(() => {
    dispatch(fetchStores({ pageSize: 200 }));
  }, [dispatch]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (storeId) next.set("store_id", storeId);
    if (startDate) next.set("start_date", startDate);
    if (endDate) next.set("end_date", endDate);
    if (page && page !== 1) next.set("page", String(page));
    setSearchParams(next, { replace: true });
  }, [storeId, startDate, endDate, page, setSearchParams]);

  useEffect(() => {
    dispatch(fetchReturns({ storeId: storeId || undefined, startDate, endDate, page, pageSize: PAGE_SIZE }));
  }, [dispatch, storeId, startDate, endDate, page]);

  const rows = useMemo(() => flattenToProductRows(transactions), [transactions]);

  const resetFilters = () => {
    setStoreId("");
    setStartDate(firstDayOfMonth());
    setEndDate(today());
    setPage(1);
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Возвраты</div>
          <div style={styles.subTitle}>Список возвращённых товаров по магазинам — товар, дата, количество, причина, кто оформил.</div>
        </div>

        <div style={styles.headerActions}>
          <Select
            value={storeId}
            onChange={(v) => { setStoreId(v); setPage(1); }}
            options={[
              { value: "", label: "Все магазины" },
              ...stores.map((s) => ({ value: String(s.id), label: s.name })),
            ]}
          />
          <input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            style={styles.dateInput}
          />
          <span style={styles.dateSep}>—</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            style={styles.dateInput}
          />
          <button type="button" onClick={resetFilters} style={styles.btn}>
            Сбросить
          </button>
        </div>
      </div>

      {loadStatus === "loading" && <Loading text="Загрузка…" />}
      {loadStatus === "failed" && <div style={styles.error}>{error}</div>}

      <div style={styles.body}>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Дата/время</th>
                <th style={styles.th}>Магазин</th>
                <th style={styles.th}>Товар</th>
                <th style={styles.th}>Штрихкод</th>
                <th style={styles.th}>Количество</th>
                <th style={styles.th}>Причина</th>
                <th style={styles.th}>Кто оформил</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && loadStatus === "succeeded" ? (
                <tr>
                  <td colSpan={7} style={styles.tdMuted}>
                    Возвратов за период не найдено
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.key} style={styles.tr}>
                    <td style={styles.td}>{formatDateTime(r.transactionDt)}</td>
                    <td style={styles.td}>{r.storeName}</td>
                    <td style={styles.td}>{r.productName}</td>
                    <td style={styles.tdMono}>{r.barcode}</td>
                    <td style={styles.tdMono}>{r.quantity}</td>
                    <td style={styles.td}>{r.reasonLabel}</td>
                    <td style={styles.td}>{r.cashierName}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalCount > 0 && (
          <PaginationBar
            page={returnsPage}
            totalCount={totalCount}
            pageSize={returnsPageSize}
            itemsOnPage={transactions.length}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { height: "100%", display: "flex", flexDirection: "column", background: "var(--c-bg)", color: "var(--c-text)" },
  header: { padding: "16px 24px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 14, flexWrap: "wrap", flexShrink: 0 },
  title: { fontSize: 22, fontWeight: 900, color: "var(--c-text)", margin: 0 },
  subTitle: { marginTop: 3, fontSize: 15, color: "var(--c-text-muted)", lineHeight: 1.35, maxWidth: 760 },
  headerActions: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  select: { padding: "8px 12px", borderRadius: 8, border: "1px solid var(--c-border)", background: "var(--c-input)", color: "var(--c-text)", outline: "none", fontSize: 15 },
  dateInput: { padding: "8px 12px", borderRadius: 8, border: "1px solid var(--c-border)", background: "var(--c-input)", color: "var(--c-text)", outline: "none", fontSize: 15 },
  dateSep: { color: "var(--c-text-muted)" },
  btn: { padding: "8px 14px", borderRadius: 99, border: "1px solid var(--c-border-light)", background: "transparent", color: "var(--c-text-sub)", cursor: "pointer", fontWeight: 600, fontSize: 15 },
  notice: { padding: "14px 24px", fontSize: 15, color: "var(--c-text-muted)" },
  error: { padding: "12px 24px", fontSize: 15, background: "rgba(248,113,113,0.1)", borderTop: "1px solid rgba(248,113,113,0.2)", borderBottom: "1px solid rgba(248,113,113,0.2)", color: "var(--c-danger)" },
  body: { padding: "16px 24px", flex: 1, minHeight: 0, overflow: "auto" },
  tableWrap: { border: "1px solid var(--c-border)", background: "var(--c-card)", borderRadius: 16, overflow: "hidden" },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
  th: { textAlign: "left", padding: "10px 14px", fontSize: 13, fontWeight: 700, color: "var(--c-text-sub)", background: "var(--c-input)", borderBottom: "1px solid var(--c-border)", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.4px" },
  tr: {},
  td: { padding: "10px 14px", fontSize: 15, borderBottom: "1px solid var(--c-border)", verticalAlign: "middle", color: "var(--c-text)" },
  tdMono: { padding: "10px 14px", fontSize: 15, borderBottom: "1px solid var(--c-border)", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace", color: "var(--c-text)" },
  tdMuted: { padding: 14, fontSize: 15, color: "var(--c-text-muted)" },
};
